
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAnalytics } from "@/hooks/use-analytics";
import { useState } from "react";
import { isValidBookingRef } from "@/utils/validation";

interface BookingReferenceCardProps {
  bookingRef: string;
  lastName: string;
  tripTitle?: string;
  tripType: string;
  onBookingRefChange: (value: string) => void;
  onLastNameChange: (value: string) => void;
  onTripTitleChange?: (value: string) => void;
  onTripTypeChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
}

export const BookingReferenceCard = ({
  bookingRef,
  lastName,
  tripTitle,
  tripType,
  onBookingRefChange,
  onLastNameChange,
  onTripTitleChange,
  onTripTypeChange,
  onSubmit
}: BookingReferenceCardProps) => {
  const { t } = useTranslation();
  const { trackEvent } = useAnalytics();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  const handleBookingRefChange = (value: string) => {
    const newValue = value.toUpperCase();
    onBookingRefChange(newValue);
    
    if (newValue.length > 0 && newValue.length < 6) {
      setErrorMessage(t("newTrip.bookingRefTooShort"));
    } else if (newValue.length > 10) {
      setErrorMessage(t("newTrip.bookingRefTooLong"));
    } else {
      setErrorMessage(null);
    }
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (bookingRef.length < 6 || bookingRef.length > 10) {
      setErrorMessage(bookingRef.length < 6 ? 
        t("newTrip.bookingRefTooShort") : 
        t("newTrip.bookingRefTooLong"));
      return;
    }
    
    trackEvent('NewTrip', 'submit_booking_reference_form', bookingRef);
    onSubmit(e);
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("newTrip.bookingReference")}</CardTitle>
        <CardDescription>{t("newTrip.bookingReferenceDesc")}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Input 
              placeholder={t("newTrip.bookingRefPlaceholder")} 
              value={bookingRef} 
              onChange={(e) => {
                const value = e.target.value.toUpperCase();
                if (value !== bookingRef) {
                  trackEvent('NewTrip', 'booking_ref_input', value.length > 0 ? 'typing' : 'cleared');
                  handleBookingRefChange(value);
                }
              }} 
              minLength={6}
              maxLength={10} 
              onFocus={() => trackEvent('NewTrip', 'focus_booking_ref')}
              className={errorMessage ? "border-red-500" : ""}
            />
            {errorMessage && (
              <p className="text-sm text-red-500">{errorMessage}</p>
            )}
          </div>
          <Button 
            type="submit" 
            className="w-full bg-[#000835] hover:bg-[#001050]"
            onClick={() => trackEvent('NewTrip', 'click_find_booking', bookingRef)}
            disabled={bookingRef.length < 6 || bookingRef.length > 10}
          >
            {t("newTrip.findBooking")}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
